/* eslint-disable react/require-default-props */
import React from 'react'
import styled, { css } from 'styled-components'
import { ReactComponent as GaugeChartNoUnit } from 'libs/shared/assets/src/lib/images/half-doughnut.svg'
import { Tooltip } from '@dataplace.ai/ui-components/atoms'
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'

const DoughnutWrapper = styled.div<{$isPrinting?: boolean}>(({ $isPrinting = false }) => css`
  height: ${$isPrinting ? '55px' : '110px'};
  margin: auto;

  > svg {
    margin: 0 auto;
    width: 100%;
    height: ${$isPrinting ? '55px' : '110px'};
  }
`)

const IndicatorHandle = styled.div`
  position: relative;
  width: 0;
  margin: 0 auto;
  bottom: 1rem;
`

const Value = styled.div(({
  theme: {
    palette, typography,
  },
}) => css`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  cursor: pointer;
  animation: appear 2s;

  > span:first-child {
    color: ${palette.black};
    font-size: ${typography.big.pt_22_semibold.fontSize};
    font-weight: ${typography.big.pt_22_semibold.fontWeight};
    line-height: ${typography.big.pt_22_semibold.lineHeight};
  }

  @keyframes appear {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

`)

const Indicator = styled.span<{ value: number, $isPrinting?: boolean }>(({
  value, theme: { palette }, $isPrinting = false,
}) => {
  const rotationValue = (value * 0.9) - 90

  return css`
    position: absolute;
    display: flex;
    width: 7px;
    height: ${$isPrinting ? '45px' : '90px'};
    background-color: ${palette.light.white};
    bottom: ${$isPrinting ? '-27px' : '-44px'};
    left: -3px;
    transform: ${`rotate(${rotationValue}deg) translateY(-50%)`};
    animation-name: rotation;
    animation-duration: 1s;

    > span:first-child {
      min-width: 3px;
      background-color: black;
      margin: 0 2px;
      position: relative;

      :after {
        content: '';
        min-width: 8px;
        height: 8px;
        background-color: ${palette.black};
        border-radius: 50%;
        transform: translateX(calc(-50% + 1px)) translateY(calc(-50%));
        display: flex;
      }

      :before {
        content: '';
        min-width: 8px;
        height: 8px;
        background-color: ${palette.black};
        border-radius: 50%;
        transform: translateX(calc(-50% + 1px)) translateY(calc(70%));
        display: flex;
        position: absolute;
        bottom: 0;
      }
    }

    > div:nth-child(2) {
      transform: ${`rotate(${rotationValue * -1}deg)`};
      top: -45px;
    }

    @media screen {
      @keyframes rotation {
        from {
          transform: rotate(-90deg) translateY(-50%);
        }
        to {
          transform: ${`rotate(${rotationValue}) translateY(-50%)`};
        }
      }
    }
  `
})

const FlexWrapper = styled.div(({ theme: { palette } }) => css`
    width: fit-content;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    color: ${palette.black};

    > span:first-of-type {
      padding-left: 1rem;
    }

    > span:last-of-type {
      padding-right: 1rem;
    }
`)

const TooltipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  color: white;
  max-width: 180px;
`

interface IGaugeChartProps {
  value: number;
  unit?: string;
  tooltipText?: string;
}

function OverviewGaugeChart({
  value,
  unit,
  tooltipText,
}: IGaugeChartProps): JSX.Element {
  // Cap value at 200 for the 0-200 range
  const cappedValue = Math.min(value, 200)
  const { isPrinting } = useCheckIfPrinting()

  return (
    <FlexWrapper>
      <DoughnutWrapper $isPrinting={isPrinting}>
        <GaugeChartNoUnit />
        <IndicatorHandle>
          <Indicator
            $isPrinting={isPrinting}
            value={cappedValue}
          >
            {/* do not remove this span */}
            <span />
          </Indicator>
        </IndicatorHandle>
      </DoughnutWrapper>
      <Tooltip
        content={<TooltipWrapper>{tooltipText}</TooltipWrapper>}
        position='bottom center'
      >
        <Value>
          <span>
            {`${parseFloat(value.toFixed(2))}${unit}`}
          </span>
        </Value>
      </Tooltip>
    </FlexWrapper>
  )
}

export default OverviewGaugeChart
